import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Custom components
import toastManager from "../../../helpers/toastManager";
import AuthenticationCard from "../../../components/Authentication/AuthenticationCard";
import {
  RegularField,
  PasswordField,
} from "../../../components/InputFields/InputFields";
import PasswordRequirementsCard from "../../../components/Authentication/PasswordRequirementsCard";

// API imports
import useAxios from "../../../hooks/useAxios";
import jsonFormat from "../../../apis/jsonFormat";

// Helpers
import { Base64 } from "js-base64";

// Detect enter press
import { useEnterKeyPress } from "../../../hooks/useEnterKeyPress";

const CompleteRegistrationScreen = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [usernameWarning, setUsernameWarning] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [firstNameWarning, setFirstNameWarning] = useState(false);
  const [lastName, setLastName] = useState("");
  const [lastNameWarning, setLastNameWarning] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordWarning, setPasswordWarning] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [passwordConfirmWarning, setPasswordConfirmWarning] = useState(false);

  // Password Verifier
  const [lowercasePresent, setLowercasePresent] = useState(false);
  const [uppercasePresent, setUppercasePresent] = useState(false);
  const [numberPresent, setNumberPresent] = useState(false);
  const [specialPresent, setSpecialPresent] = useState(false);
  const [lengthPresent, setLengthPresent] = useState(false);
  const [matchingPresent, setMatchingPresent] = useState(false);

  const { user_id } = useParams();
  const { token } = useParams();
  const { user_email } = useParams();

  const base64_email = user_email.replace(/-/g, "+").replace(/_/g, "/");
  const decoded_email = Base64.decode(base64_email);

  // API Start
  const { response, error, axiosFetch } = useAxios();

  const handleSubmit = () => {
    // Verify no fields are empty.
    if (username && firstName && lastName && password && passwordConfirm) {
      // Verify password meets standards.
      if (
        lowercasePresent &&
        uppercasePresent &&
        numberPresent &&
        specialPresent &&
        lengthPresent &&
        matchingPresent
      ) {
        axiosFetch({
          axiosInstance: jsonFormat(),
          method: "PUT",
          url: "user/register",
          sendSignal: false,
          requestConfig: {
            username: username,
            password: password,
            password_confirm: passwordConfirm,
            first_name: firstName,
            last_name: lastName,

            user_id: user_id,
            token: token,

            authentication_origin: "clinical_dashboard",
          },
        });
      } else {
        toastManager(
          "warning",
          "Account cannot be created.",
          "Password must meet security standards."
        );
      }
    } else {
      toastManager(
        "warning",
        "Account cannot be created.",
        "Please fill out all fields properly."
      );
    }

    !username ? setUsernameWarning(true) : setUsernameWarning(false);
    !firstName ? setFirstNameWarning(true) : setFirstNameWarning(false);
    !lastName ? setLastNameWarning(true) : setLastNameWarning(false);
    !password ? setPasswordWarning(true) : setPasswordWarning(false);
    !passwordConfirm
      ? setPasswordConfirmWarning(true)
      : setPasswordConfirmWarning(false);
  };

  // RESPONSE Detection
  useEffect(() => {
    // 1: Custom action code handling.
    // Account successfully created.
    if (response) {
      if (response?.action_code === "account_created") {
        toastManager(
          "success",
          "Account created!",
          "Log in using your username and password."
        );
        navigate("/login");
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // 1: Custom action code handling.
      // Username errors
      if (error?.username) {
        if (error?.username?.action_code === "username_taken") {
          toastManager(
            "warning",
            "Username Taken!",
            "This username is already taken. Use a different username."
          );
        }
      }
      // User_id errors
      if (error?.user_id) {
        if (error?.user_id?.action_code === "invalid_user_id") {
          toastManager(
            "error",
            "Invalid Username!",
            "Username id does not match to any users in database."
          );
        }
      }
      // Password errors
      if (error?.password) {
        if (error?.password?.action_code === "password_issue") {
          toastManager("error", "Password Issue!", "Passwords do not match.");
        }
      }

      // Token errors
      if (error?.token) {
        if (
          error?.token?.action_code === "token_invalid" ||
          error?.token?.action_code === "authentication_failed" ||
          error?.token?.action_code === "unknown_token" ||
          error?.token?.action_code === "token_expired"
        ) {
          toastManager(
            "error",
            "Invalid Authorization!",
            "Invalid token. Request a new registration link and use the latest one."
          );
        }
      }
    }
  }, [error]);
  // API End

  // Detect enter press
  useEnterKeyPress(handleSubmit);

  return (
    <AuthenticationCard
      AuthenticationPromptTitle={"Complete Registration"}
      AuthenticationPromptText={"Fill out the details below to get started."}
      AuthenticationButtonPrompt={"Create Account"}
      AuthenticationButtonFunction={handleSubmit}
      redirectOption={"login"}
    >
      <p className="text-base text-neutral-500">{decoded_email}</p>

      <RegularField
        fieldLabel="Username"
        placeholderText="Enter a username"
        value={username}
        fieldWarning={usernameWarning}
        customOnChange={(event) => {
          // Prevent spaces
          let newUsername = event.target.value.replace(/\s/g, "");
          setUsername(newUsername);
        }}
      />

      <RegularField
        fieldLabel="First Name"
        placeholderText="Enter your first name"
        setFunction={setFirstName}
        value={firstName}
        fieldWarning={firstNameWarning}
      />

      <RegularField
        fieldLabel="Last Name"
        placeholderText="Enter your last name"
        setFunction={setLastName}
        value={lastName}
        fieldWarning={lastNameWarning}
      />

      <PasswordRequirementsCard
        password={password}
        setPasswordWarning={setPasswordWarning}
        passwordConfirm={passwordConfirm}
        setPasswordConfirmWarning={setPasswordConfirmWarning}
        lowercasePresent={lowercasePresent}
        setLowercasePresent={setLowercasePresent}
        uppercasePresent={uppercasePresent}
        setUppercasePresent={setUppercasePresent}
        numberPresent={numberPresent}
        setNumberPresent={setNumberPresent}
        specialPresent={specialPresent}
        setSpecialPresent={setSpecialPresent}
        lengthPresent={lengthPresent}
        setLengthPresent={setLengthPresent}
        matchingPresent={matchingPresent}
        setMatchingPresent={setMatchingPresent}
      />

      <PasswordField
        passwordLabel={"Password"}
        placeholderText={"Enter a password"}
        setFunction={setPassword}
        value={password}
        passwordWarning={passwordWarning}
      />

      <PasswordField
        passwordLabel={"Password Confirmation"}
        placeholderText={"Confirm the password"}
        setFunction={setPasswordConfirm}
        value={passwordConfirm}
        passwordWarning={passwordConfirmWarning}
      />
    </AuthenticationCard>
  );
};

export default CompleteRegistrationScreen;
